/**
 * Cart Events Module
 * Global listener for cart events - updates header cart count and shows notifications
 */

(function() {
  'use strict';

  /**
   * Check if GSAP is available
   */
  function hasGSAP() {
    return typeof gsap !== 'undefined';
  }

  /**
   * Update cart count in header with animation
   * @param {number} count - New cart item count
   */
  function updateCartCount(count) {
    const cartCountElements = document.querySelectorAll('[data-cart-count]');
    
    cartCountElements.forEach(element => {
      const oldCount = parseInt(element.textContent) || 0;
      
      // Update the text
      element.textContent = count;
      
      // Toggle visibility class
      if (count > 0) {
        element.classList.add('is-visible');
      } else {
        element.classList.remove('is-visible');
      }
      
      // Animate if count increased
      if (count > oldCount) {
        animateCartBump(element);
      }
    });
  }

  /**
   * Animate cart count with bump effect
   * @param {HTMLElement} element - Cart count element
   */
  function animateCartBump(element) {
    if (hasGSAP()) {
      // GSAP spring animation
      gsap.fromTo(element, 
        { scale: 1.4 },
        { 
          scale: 1, 
          duration: 0.5, 
          ease: 'elastic.out(1, 0.3)'
        }
      );
    } else {
      // CSS fallback
      element.classList.add('is-bumping');
      setTimeout(() => {
        element.classList.remove('is-bumping');
      }, 300);
    }
  }

  /**
   * Show cart item added notification
   * @param {Object} item - Added item data
   */
  function showAddedNotification(item) {
    if (!window.ThemeNotification) {
      console.warn('ThemeNotification not available');
      return;
    }

    // Build message
    let message = '';
    if (item.variant_title && item.variant_title !== 'Default Title') {
      message = item.variant_title;
    }
    if (item.quantity > 1) {
      message += message ? ` × ${item.quantity}` : `Qty: ${item.quantity}`;
    }

    // Get image URL (resize for notification)
    let imageUrl = item.image;
    if (imageUrl && !imageUrl.includes('no-image')) {
      // Resize Shopify image to 112x112 for retina
      imageUrl = imageUrl.replace(/(\.[^.]+)$/, '_112x112$1');
    }

    window.ThemeNotification.cart(
      'Added to Cart',
      `${item.title}${message ? ` - ${message}` : ''}`,
      {
        image: imageUrl,
        imageAlt: item.title,
        duration: 4000,
        position: 'top-right'
      }
    );
  }

  /**
   * Show cart error notification
   * @param {string} message - Error message
   */
  function showErrorNotification(message) {
    if (!window.ThemeNotification) {
      console.warn('ThemeNotification not available');
      return;
    }

    window.ThemeNotification.error(
      'Cart Error',
      message || 'Something went wrong. Please try again.',
      {
        duration: 5000,
        position: 'top-right'
      }
    );
  }

  /**
   * Show item removed notification
   * @param {Object} item - Removed item data
   */
  function showRemovedNotification(item) {
    if (!window.ThemeNotification) {
      return;
    }

    window.ThemeNotification.info(
      'Item Removed',
      item.title,
      {
        duration: 3000,
        position: 'top-right'
      }
    );
  }

  /**
   * Show quantity updated notification
   * @param {Object} data - Update data with item, oldQuantity, newQuantity
   */
  function showQuantityNotification(data) {
    if (!window.ThemeNotification) {
      return;
    }

    const { item, oldQuantity, newQuantity } = data;
    const diff = newQuantity - oldQuantity;
    const action = diff > 0 ? 'increased' : 'decreased';

    window.ThemeNotification.info(
      'Cart Updated',
      `${item.title} quantity ${action} to ${newQuantity}`,
      {
        duration: 3000,
        position: 'top-right'
      }
    );
  }

  /**
   * Initialize cart event listeners
   */
  function init() {
    // Listen for item added
    window.addEventListener('cart:item-added', (event) => {
      const { item, cart } = event.detail;
      
      // Update cart count
      if (cart && typeof cart.item_count !== 'undefined') {
        updateCartCount(cart.item_count);
      }
      
      // Notification disabled - cart drawer opens instead
    });

    // Listen for item removed
    window.addEventListener('cart:item-removed', (event) => {
      const { item, cart } = event.detail;
      
      // Update cart count
      if (cart && typeof cart.item_count !== 'undefined') {
        updateCartCount(cart.item_count);
      }
      
      // Notification disabled
    });

    // Listen for quantity updated
    window.addEventListener('cart:quantity-updated', (event) => {
      const { cart } = event.detail;
      
      // Update cart count
      if (cart && typeof cart.item_count !== 'undefined') {
        updateCartCount(cart.item_count);
      }
      
      // Optionally show notification
      // showQuantityNotification(event.detail);
    });

    // Listen for general cart updates (header count only, no notification)
    window.addEventListener('cart:updated', (event) => {
      const { cart } = event.detail;
      
      if (cart && typeof cart.item_count !== 'undefined') {
        updateCartCount(cart.item_count);
      }
    });

    // Listen for cart errors
    window.addEventListener('cart:error', (event) => {
      const { message } = event.detail;
      showErrorNotification(message);
    });

    // Also listen for legacy cart:updated events (from existing code)
    // This provides backwards compatibility during transition
    window.addEventListener('cart:updated', async (event) => {
      // If no cart data in event, fetch it
      if (!event.detail || !event.detail.cart) {
        try {
          if (window.CartAPI) {
            const cart = await window.CartAPI.getCart();
            updateCartCount(cart.item_count);
          }
        } catch (error) {
          console.error('Failed to fetch cart for update:', error);
        }
      }
    });
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose utilities for external use
  window.CartEvents = {
    updateCartCount,
    showAddedNotification,
    showErrorNotification,
    showRemovedNotification
  };
})();

